import { NumberField, numberRegex } from '@iheartjane/reefer';
import type { NumberFieldProps } from '@iheartjane/reefer';

import type { FormFieldProps } from '../formField.types';
import { FormFieldWrapper } from '../formFieldWrapper/formFieldWrapper';

export interface FormNumberFieldProps
  extends
    Omit<NumberFieldProps, 'children'>,
    Omit<FormFieldProps, 'maxLength' | 'minLength'> {}

/**
 * Number fields allow users to enter numeric values into a UI.
 *
 * Use this component *within forms* for inputs of `type`:
 * `number`.
 *
 * Optionally add a `startUnit` or `endUnit` to display a unit of measurement to the left or right of the field value.
 * Also, optionally add `maskFormat` to mask the input, e.g. `(###) ###-####`
 *
 * _Field validations supported:_ `required`, `max`, `min`, `pattern`, `step`, `validate`. *
 *
 * **NOTE: DO NOT USE THIS COMPONENT OUTSIDE FORMS**
 *
 * _For a similar component for use outside forms, see [`NumberField`](/story/components-forms-fields-numberfield--default)._
 */
export function FormNumberField({
  allowedDecimalPlaces,
  allowLeadingZeros,
  autocomplete,
  autoFocus = false,
  defaultValue,
  disabled = false,
  endUnit,
  errorMessage = 'Please enter a numeric value.',
  errorOnBlur = false,
  helperText,
  label,
  labelHidden = false,
  maskFormat,
  max,
  min,
  name,
  onBlur,
  onChange,
  pattern = numberRegex,
  placeholder,
  readOnly = false,
  required = false,
  requiredMessage,
  shouldUnregister,
  startUnit,
  step = 'any',
  'data-testid': testId,
  validate,
  width = 'auto',
  ...props
}: FormNumberFieldProps) {
  return (
    <FormFieldWrapper
      defaultValue={defaultValue}
      errorMessage={errorMessage}
      errorOnBlur={errorOnBlur}
      name={name}
      max={max}
      min={min}
      pattern={pattern}
      render={({ children, field, invalid }) => (
        <NumberField
          aria-invalid={invalid}
          allowedDecimalPlaces={allowedDecimalPlaces}
          allowLeadingZeros={allowLeadingZeros}
          autoFocus={autoFocus}
          defaultValue={defaultValue}
          autocomplete={autocomplete}
          disabled={disabled}
          helperText={helperText}
          maskFormat={maskFormat}
          label={label}
          labelHidden={labelHidden}
          max={max}
          min={min}
          name={field.name}
          onBlur={(value?: number) => {
            onBlur && onBlur(value);
            field.onBlur();
          }}
          onChange={(value?: number) => {
            onChange && onChange(value);
            field.onChange(value === undefined ? '' : value);
          }}
          onUpdate={onChange}
          pattern={pattern}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          ref={field.ref}
          step={step}
          startUnit={startUnit}
          endUnit={endUnit}
          data-testid={testId}
          value={field.value}
          width={width}
          {...props}
        >
          {children}
        </NumberField>
      )}
      required={required}
      requiredMessage={requiredMessage}
      shouldUnregister={shouldUnregister}
      validate={validate}
    />
  );
}
